// ============================================
// src/app/pages/onboarding/services/onboarding-state.service.ts
// ============================================
// Gestion de l'état global de l'onboarding (persisté en sessionStorage)

import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  OnboardingState,
  FixedMerchantData,
  ItinerantMerchantData,
  MerchantType,
} from '../models/onboarding.model';
import { AuthService } from './auth.service';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingStateService {
  private readonly STORAGE_KEY = 'onboardingState';
  private readonly API_URL = '/api/onboarding';

  private httpClient = inject(HttpClient);
  private authService = inject(AuthService);
  private log = inject(LogService);

  // État initial (NE PAS mettre merchantType par défaut)
  private readonly initialState: OnboardingState = {
    currentStep: 1,
    merchantType: null,
    formData: {},
    isStepComplete: false,
    isLoading: false,
  };

  // Flag pour éviter de charger 2 fois
  private loadedFromBackend = false;

  // =========================
  // Signal state
  // =========================
  private _state = signal<OnboardingState>(this.initialState);
  readonly state = this._state.asReadonly();

  private _currentStep = signal<number>(1);
  readonly currentStep = this._currentStep.asReadonly();

  private _merchantType = signal<MerchantType | null>(null);
  readonly merchantType = this._merchantType.asReadonly();

  private _formData = signal<any>({});
  readonly formData = this._formData.asReadonly();

  private _isStepComplete = signal<boolean>(false);
  readonly isStepComplete = this._isStepComplete.asReadonly();

  constructor() {
    this.loadState();
  }

  // =========================
  // Persistence
  // =========================
  private persistState(): void {
    const state: OnboardingState = {
      currentStep: this._currentStep() as any,
      merchantType: this._merchantType(),
      formData: this._formData(),
      isStepComplete: this._isStepComplete(),
      isLoading: this._state().isLoading,
      error: this._state().error,
    };

    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private loadState(): void {
    const saved = sessionStorage.getItem(this.STORAGE_KEY);
    if (!saved) return;

    try {
      const state: OnboardingState = JSON.parse(saved);

      this._state.set(state);
      this._currentStep.set(state.currentStep);
      this._merchantType.set(state.merchantType ?? null);
      this._formData.set(state.formData ?? {});
      this._isStepComplete.set(state.isStepComplete ?? false);

      this.log.debug('Onboarding state restored from sessionStorage');
    } catch (err) {
      this.log.error('Failed to restore onboarding state', {
        error: (err as Error).message,
      });
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  // =========================
  // Mutateurs d'état
  // =========================
  setCurrentStep(step: number): void {
    const newState: OnboardingState = {
      ...this._state(),
      currentStep: step as any,
    };

    this._state.set(newState);
    this._currentStep.set(step);
    this.persistState();
  }

  setMerchantType(type: MerchantType): void {
    const newState: OnboardingState = {
      ...this._state(),
      merchantType: type,
    };

    this._state.set(newState);
    this._merchantType.set(type);
    this.persistState();
  }

  updateFormData(data: any): void {
    const updated = { ...this._formData(), ...data };

    const newState: OnboardingState = {
      ...this._state(),
      formData: updated,
    };

    this._formData.set(updated);
    this._state.set(newState);
    this.persistState();
  }

  setStepComplete(isComplete: boolean): void {
    const newState: OnboardingState = {
      ...this._state(),
      isStepComplete: isComplete,
    };

    this._isStepComplete.set(isComplete);
    this._state.set(newState);
    this.persistState();
  }

  setIsLoading(isLoading: boolean): void {
    const newState: OnboardingState = {
      ...this._state(),
      isLoading,
    };

    this._state.set(newState);
    this.persistState();
  }

  setError(error: string | undefined): void {
    const newState: OnboardingState = {
      ...this._state(),
      error,
    };

    this._state.set(newState);
    this.persistState();
  }

  // =========================
  // Getters synchrones
  // =========================
  getState(): OnboardingState {
    return this._state();
  }

  getFormData(): any {
    return this._formData();
  }

  getMerchantType(): MerchantType | null {
    return this._merchantType();
  }

  getActivityType(): string | null {
    return this._formData()?.activityType || null;
  }

  // =========================
  // Mapping métier
  // =========================
  getFixedMerchantData(): FixedMerchantData {
    const data = this._formData();
    return {
      merchantType: MerchantType.FIXED,
      activityType: data.activityType,
      affluence: data.affluence,
      coverage: data.coverage,
      teamSize: data.teamSize,
      services: data.services,
      periodicity: data.periodicity,
      clusterId: data.clusterId,
    };
  }

  getItinerantMerchantData(): ItinerantMerchantData {
    const data = this._formData();
    return {
      merchantType: MerchantType.ITINERANT,
      activityType: data.activityType,
      eventTypes: data.eventTypes,
      coverage: data.coverage,
      teamSize: data.teamSize,
      clusterId: data.clusterId,
    };
  }

  // =========================
  // Reset
  // =========================
  resetState(): void {
    this.log.debug('Resetting onboarding state');

    this._state.set(this.initialState);
    this._currentStep.set(1);
    this._merchantType.set(null);
    this._formData.set({});
    this._isStepComplete.set(false);
    this.loadedFromBackend = false;

    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  clearOnboardingState(): void {
    this.resetState();
  }

  // =========================
  // Chargement depuis le Backend
  // =========================
  /**
   * Charge les données d'onboarding depuis le backend (DB)
   * Permet de pré-remplir les formulaires si l'utilisateur revient
   * après avoir quitté (nouveau navigateur, session expirée, etc.)
   */
  loadFromBackend(): Observable<boolean> {
    // Éviter de charger plusieurs fois
    if (this.loadedFromBackend) {
      return of(true);
    }

    // Vérifier si l'utilisateur est connecté
    const token = this.authService.getToken();
    if (!token) {
      this.log.debug('Pas de token, skip chargement backend');
      return of(false);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.httpClient
      .get<OnboardingStatusResponse>(`${this.API_URL}/status`, { headers })
      .pipe(
        tap((data) => {
          this.log.debug('Données onboarding chargées depuis DB');
          this.loadedFromBackend = true;

          // Toujours hydrater si DB a des données métier et local n'en a pas
          const localData = this._formData();
          const localHasMerchantType = !!localData?.merchantType;
          const dbHasMerchantType = !!data.merchantType;

          if (dbHasMerchantType && !localHasMerchantType) {
            this.log.debug('DB plus complète que local, hydratation');
            this.hydrateFromBackend(data);
          } else if (dbHasMerchantType) {
            // Même si local a des données, fusionner avec DB (DB = source de vérité)
            this.log.debug('Fusion données DB avec local');
            this.hydrateFromBackend(data);
          }
        }),
        catchError((err) => {
          this.log.warn('Impossible de charger onboarding depuis DB', {
            error: (err as Error).message,
          });
          return of(false);
        }),
      ) as Observable<boolean>;
  }

  /**
   * Hydrate l'état local avec les données du backend
   */
  private hydrateFromBackend(data: OnboardingStatusResponse): void {
    this.log.debug('Hydratation onboarding depuis DB');

    // Mapper merchantType string -> enum
    const merchantType =
      data.merchantType === 'fixed'
        ? MerchantType.FIXED
        : data.merchantType === 'itinerant'
          ? MerchantType.ITINERANT
          : null;

    // Construire formData avec TOUTES les données
    const formData: any = {};

    // IMPORTANT: merchantType doit être dans formData pour les composants
    if (merchantType) formData.merchantType = merchantType;

    if (data.activityType) formData.activityType = data.activityType;
    if (data.positioning) formData.positioning = data.positioning;
    if (data.coverage) formData.coverage = data.coverage;
    if (data.teamSize) formData.teamSize = data.teamSize;
    if (data.affluence) formData.affluence = data.affluence;
    if (data.periodicity) formData.periodicity = data.periodicity;
    if (data.services?.length) formData.services = data.services;
    if (data.eventTypes?.length) formData.eventTypes = data.eventTypes;

    // Adresse pour le formulaire de localisation
    if (data.location?.address) {
      formData.address = {
        address: data.location.address,
        formatted: data.location.address,
        city: data.location.city,
        postalCode: data.location.postcode,
        country: data.location.country || 'France',
        coordinates: data.location.coordinates,
      };
    }

    this.log.debug('FormData hydraté');

    // Déterminer l'étape actuelle
    const step = data.onboardingStep ?? this.determineStepFromData(data);

    // Mettre à jour l'état
    if (merchantType) {
      this._merchantType.set(merchantType);
    }
    this._formData.set(formData);
    this._currentStep.set(step);

    const newState: OnboardingState = {
      currentStep: step as any,
      merchantType,
      formData,
      isStepComplete: false,
      isLoading: false,
    };
    this._state.set(newState);
    this.persistState();

    this.log.debug('État onboarding restauré depuis DB', {
      step: newState.currentStep,
    });
  }

  /**
   * Détermine l'étape à afficher en fonction des données disponibles
   */
  private determineStepFromData(data: OnboardingStatusResponse): number {
    // Si onboarding complété, aller au résumé
    if (data.onboardingCompleted) return 6;

    // Sinon, trouver la première étape incomplète
    if (!data.activityType) return 1;
    if (!data.merchantType) return 2;
    if (!data.teamSize && !data.services?.length && !data.eventTypes?.length)
      return 3;
    // Étape 4 = produits, étape 5 = localisation
    return data.onboardingStep || 4;
  }
}

/**
 * Interface pour la réponse du backend
 */
interface OnboardingStatusResponse {
  onboardingStep: number | null;
  onboardingCompleted: boolean;
  merchantType: string | null;
  activityType: string | null;
  positioning: string | null;
  coverage: string | null;
  teamSize: string | null;
  affluence: string | null;
  periodicity: string | null;
  services: string[];
  eventTypes: string[];
  businessName: string | null;
  locationCity: string | null;
  locationRegion: string | null;
  location: {
    address: string | null;
    city: string | null;
    postcode: string | null;
    country: string | null;
    coordinates: { lat: number; lng: number } | null;
  } | null;
}
